import React, { useState } from 'react';
import { Plus, Trash2, Copy, HelpCircle, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { PageSection } from '../../../../types';

interface FaqEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const FaqEditor: React.FC<FaqEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};
  const items: any[] = content.items || [
    {
      id: '1',
      title: 'Hizmet veya kiralama süreci nasıl işliyor?',
      description: 'Talebinizi ilettikten sonra uzman danışmanımız sizinle iletişime geçer ve süreç planlanır.',
    },
    {
      id: '2',
      title: 'Ödeme ve iptal koşulları nelerdir?',
      description: 'Tüm işlemler resmi sözleşme ve güvenli ödeme yöntemleriyle güvence altına alınır.',
    },
  ];

  const [activeItemId, setActiveItemId] = useState<string | null>(items[0]?.id || null);

  const handleUpdateItem = (index: number, updates: any) => {
    const next = [...items];
    next[index] = { ...next[index], ...updates };
    onUpdateContent({ items: next });
  };

  const handleAddItem = () => {
    const newItem = {
      id: `faq_${Date.now().toString(36)}`,
      title: `Yeni Soru ${items.length + 1}?`,
      description: 'Bu sorunun detaylı yanıtı...',
    };
    const next = [...items, newItem];
    onUpdateContent({ items: next });
    setActiveItemId(newItem.id);
  };

  const handleDuplicateItem = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const source = items[index];
    const duplicated = {
      ...JSON.parse(JSON.stringify(source)),
      id: `faq_${Date.now().toString(36)}`,
      title: `${source.title} (Kopya)`,
    };
    const next = [...items];
    next.splice(index + 1, 0, duplicated);
    onUpdateContent({ items: next });
    setActiveItemId(duplicated.id);
  };

  const handleDeleteItem = (index: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = items.filter((_, i) => i !== index);
    onUpdateContent({ items: next });
    if (activeItemId === items[index]?.id) {
      setActiveItemId(next[0]?.id || null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Headings */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">SSS Başlıkları</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge ?? 'SIKÇA SORULAN SORULAR'}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Başlık</label>
          <input
            type="text"
            value={content.title ?? 'Aklınıza Takılan Soruların Yanıtları'}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>
      </div>

      {/* Questions list */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-4 h-4 text-blue-400" />
            <h4 className="text-xs font-extrabold text-slate-200">Soru & Cevaplar ({items.length})</h4>
          </div>
          <button
            type="button"
            onClick={handleAddItem}
            className="px-2.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-bold flex items-center gap-1.5 shadow-sm transition"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Soru Ekle</span>
          </button>
        </div>

        <div className="space-y-2.5">
          {items.map((item, idx) => {
            const isExpanded = activeItemId === item.id;

            return (
              <div
                key={item.id || idx}
                className={`rounded-2xl border transition-all ${
                  isExpanded
                    ? 'bg-slate-800/90 border-blue-500/50 shadow-md ring-1 ring-blue-500/20'
                    : 'bg-slate-800/40 border-slate-700/60 hover:border-slate-600'
                }`}
              >
                <div
                  onClick={() => setActiveItemId(isExpanded ? null : item.id)}
                  className="p-3 flex items-center justify-between cursor-pointer select-none"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-6 h-6 rounded-lg bg-slate-900 border border-slate-700 flex items-center justify-center text-[10px] font-black text-slate-300">
                      ?
                    </span>
                    <div className="text-xs font-bold text-slate-200 truncate">{item.title}</div>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => handleDuplicateItem(idx, e)}
                      title="Kopyala"
                      className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDeleteItem(idx, e)}
                      title="Sil"
                      className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                    <div className="p-1 text-slate-400">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="p-4 pt-2 border-t border-slate-700/50 space-y-3.5">
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Soru Metni</label>
                      <input
                        type="text"
                        value={item.title || ''}
                        onChange={(e) => handleUpdateItem(idx, { title: e.target.value })}
                        className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Cevap Açıklaması</label>
                      <textarea
                        value={item.description || ''}
                        onChange={(e) => handleUpdateItem(idx, { description: e.target.value })}
                        rows={3}
                        className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2 rounded-xl outline-none focus:border-blue-500 resize-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
